import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface SettingsTab {
  id: string;
  component: React.ReactNode;
}

interface SettingsSectionRendererProps {
  tabs: SettingsTab[];
}

const DEFAULT_SECTION_KEY = 'appearance';

/**
 * Purpose: Renders the appropriate settings content component based on the 'settings_section' query parameter.
 * Location: src/components/features/settings/SettingsSectionRenderer.tsx
 * Constraints: To be used within a layout that provides the query parameter context (e.g., SettingsPageLayout).
 */
function SettingsSectionRenderer({ tabs }: SettingsSectionRendererProps) {
  const location = useLocation();
  const [activeTabContent, setActiveTabContent] =
    useState<React.ReactNode>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sectionQueryParam = searchParams.get('settings_section');
    const currentTab = tabs.find(
      (tab) => tab.id === (sectionQueryParam || DEFAULT_SECTION_KEY)
    );

    if (currentTab) {
      setActiveTabContent(currentTab.component);
    } else {
      const defaultTab = tabs.find((tab) => tab.id === DEFAULT_SECTION_KEY);
      if (defaultTab) {
        setActiveTabContent(defaultTab.component);
      } else {
        setActiveTabContent(
          <div className="p-6">
            Invalid settings section or content not found.
          </div>
        );
      }
    }
  }, [location.search, tabs]);

  return <>{activeTabContent}</>;
}

export default SettingsSectionRenderer;
